/**
 * Call History Component
 * - Shows completed calls with recordings and summaries
 * - Used on both Observer and Principal dashboards
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Phone, Clock, Calendar, Play, Pause, Download, FileText,
  Loader2, RefreshCw, ChevronDown, ChevronUp, Baby, User,
  Volume2, MessageSquare, CheckCircle, AlertCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CallHistory({ userType = 'observer', token }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [expandedCall, setExpandedCall] = useState(null);

  const callKey = (call) => call.id || call.call_id || call.child_id + '_' + call.created_at;
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioRef, setAudioRef] = useState(null);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const endpoint = userType === 'observer' 
        ? `${API_URL}/api/observer/call-history?token=${token}`
        : `${API_URL}/api/principal/call-history?token=${token}`;
      
      const response = await axios.get(endpoint);
      setCalls(response.data.calls || []);
    } catch (err) {
      console.error('Error loading calls:', err);
      toast({
        title: "Error",
        description: "Failed to load call history",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadCalls();
    }
  }, [token]);

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const playRecording = (call) => {
    const key = callKey(call);
    if (playingAudio === key) {
      if (audioRef) audioRef.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef) audioRef.pause();
      const audio = new Audio(call.recording_url);
      audio.play();
      audio.onended = () => setPlayingAudio(null);
      setAudioRef(audio);
      setPlayingAudio(key);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'answered': return 'bg-green-100 text-green-700';
      case 'no_answer': return 'bg-yellow-100 text-yellow-700';
      case 'busy': return 'bg-orange-100 text-orange-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-sanjaya-orange" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Call History</h2>
          <p className="text-sm text-slate-500">View recordings and summaries of completed calls</p>
        </div>
        <Button variant="outline" onClick={loadCalls} size="sm">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Calls List */}
      <Card>
        <CardContent className="p-0">
          {calls.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Phone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No call history yet</p>
              <p className="text-sm">Completed calls will appear here with recordings</p>
            </div>
          ) : (
            <div className="divide-y">
              {calls.map((call) => (
                <div key={callKey(call)} className="p-4 hover:bg-slate-50">
                  {/* Call Header */}
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedCall(expandedCall === callKey(call) ? null : callKey(call))}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-sanjaya-orange/10 flex items-center justify-center">
                        <Baby className="w-6 h-6 text-sanjaya-orange" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{call.child_name}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(call.triggered_at || call.completed_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDuration(call.duration_seconds)}
                          </span>
                          {userType === 'principal' && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {call.observer_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Recording indicator */}
                      {call.recording_url && (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <Volume2 className="w-4 h-4" />
                          Recording
                        </span>
                      )}
                      
                      {/* Status badge */}
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(call.call_status || 'completed')}`}>
                        {call.call_status || 'Completed'}
                      </span>

                      {/* Expand icon */}
                      {expandedCall === callKey(call) ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedCall === callKey(call) && (
                    <div className="mt-4 pl-16 space-y-4">
                      {/* Recording Player */}
                      {call.recording_url ? (
                        <div className="bg-slate-50 rounded-lg p-4">
                          <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                            <Volume2 className="w-4 h-4" />
                            Call Recording
                          </h4>
                          <div className="flex items-center gap-3">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                playRecording(call);
                              }}
                              className={playingAudio === callKey(call) ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'}
                            >
                              {playingAudio === callKey(call) ? (
                                <><Pause className="w-4 h-4 mr-1" /> Pause</>
                              ) : (
                                <><Play className="w-4 h-4 mr-1" /> Play</>
                              )}
                            </Button>
                            <a 
                              href={call.recording_url} 
                              download
                              className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </a>
                            <span className="text-sm text-slate-500">
                              Duration: {formatDuration(call.duration_seconds)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 rounded-lg p-4 text-yellow-700 text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Recording not available yet. It may take a few minutes after the call ends.
                        </div>
                      )}

                      {/* Call Summary / Transcript */}
                      {call.transcript || call.summary ? (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Call Summary
                          </h4>
                          
                          {call.summary && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-slate-600 mb-1">Summary:</p>
                              <p className="text-sm text-slate-700 bg-white p-3 rounded border">
                                {call.summary}
                              </p>
                            </div>
                          )}

                          {call.transcript && (
                            <div>
                              <p className="text-sm font-medium text-slate-600 mb-1">Transcript:</p>
                              <div className="text-sm text-slate-700 bg-white p-3 rounded border max-h-48 overflow-y-auto">
                                {call.transcript}
                              </div>
                            </div>
                          )}

                          {call.key_points && call.key_points.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-slate-600 mb-1">Key Points:</p>
                              <ul className="list-disc list-inside text-sm text-slate-700">
                                {call.key_points.map((point, idx) => (
                                  <li key={idx}>{point}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 text-gray-600 text-sm flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          No summary available. Transcript will appear once the recording is processed.
                        </div>
                      )}

                      {/* Call Details */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Parent Phone</p>
                          <p className="font-medium">{call.parent_phone || '--'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Call Type</p>
                          <p className="font-medium capitalize">{call.call_type || 'Scheduled'}</p>
                        </div>
                        {call.notes && (
                          <div className="col-span-2">
                            <p className="text-slate-500">Notes</p>
                            <p className="font-medium">{call.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
